import { useEffect, useRef, useState } from 'react';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';

import { useAppSelector } from '@/store';
import { gameSelector } from '@/store/reducers/game';
import { authSelector, interfaceLanguageSelector } from '@/store/reducers/auth';
import { PopUpSummaryProps, NewGameTypes } from '@/types/game';
import { SkillTypes } from '@/types/skill';
import GameUtils from '@/utils/gameUtils';
import UserUtils from '@/utils/user';
import commonUtils from '@/utils/common';

import UseScreenSize from '@/hooks/UseScreenSize';
import UseLocalLang from '@/hooks/UseLocalLang';

import TheButton from '@/components/TheButton/TheButton';
import { getResultContent, getResultAfterFirstTry } from './helper';

import complete from '@/assets/svg/quiz-complete.svg';
import failed from '@/assets/svg/quiz-failed.svg';
import coinsRow from '@/assets/svg/coins-row.svg';
import coinsRowFailed from '@/assets/svg/coins-row-failed.svg';
import classNames from './QuizSummary.module.scss';
import QuizTip from '../../QuizTip/QuizTip';

const QuizSummary = ({
  selectedQuiz,
  selectedTopic,
  coins,
  tip,
  userData,
  correctAnswers,
  totalQuestions,
  hasNextQuiz,
  totalScore,
  isOpenWriting,
  noGrading,
  isSkipped,
  isLastActivityForCanDo,
  emitOnClosePopUp,
  emitOnExit,
  emitOnExitWithCanDo,
}: PopUpSummaryProps) => {
  const { t } = useTranslation();
  const { selectedType, gameType } = useAppSelector(gameSelector);
  const { user } = useAppSelector(authSelector);

  const { isDesktop, isMobile } = UseScreenSize();
  const [offsetFromBottom, setOffsetFromBottom] = useState(0);
  const summaryRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const interfaceLanguage = useAppSelector(interfaceLanguageSelector);
  const isTeacher = UserUtils.isTeacher(user);
  const [isVulgarFailure, setIsVulgarFailure] = useState(false);

  const isStudent = UserUtils.isStudent(user);

  const localLang = UseLocalLang();

  const score = totalScore ?? 0;

  const starsAmount = isVulgarFailure
    ? 0
    : isOpenWriting && noGrading
      ? 3
      : GameUtils.getStarsAmount(score);

  const isFailed = !isSkipped && starsAmount === 0;
  const isQuizPassedBefore = userData?.isCompleted ?? false;

  const hasFailed = isFailed || isVulgarFailure;

  const bottomHeight = bottomRef.current?.clientHeight;

  useEffect(() => {
    const hasFailedByAttempts = sessionStorage.getItem('openWritingAttempts');
    if (hasFailedByAttempts === 'true') {
      setIsVulgarFailure(true);
    }
    sessionStorage.removeItem('openWritingAttempts');
  }, [isFailed]);

  useEffect(() => {
    const offsetTop = summaryRef.current?.getBoundingClientRect().top;

    if (bottomHeight && offsetTop && isMobile) {
      setOffsetFromBottom(bottomHeight + offsetTop);
    }
  }, [bottomHeight, isMobile]);

  // isLastActivityForCanDo is computed in UseQuiz.tsx before backend/progress updates.
  const isLastActivity = isLastActivityForCanDo ?? false;

  const getButtonConfig = () => {
    if (hasFailed) {
      return {
        text: t('tryAgain'),
        showArrow: false,
        onClick: () => emitOnClosePopUp?.(hasFailed),
      };
    }

    if (hasNextQuiz) {
      return {
        text: isLastActivity ? t('rateYourself') : t('nextQuiz'),
        showArrow: !isLastActivity,
        onClick: isLastActivity ? emitOnExitWithCanDo : () => emitOnClosePopUp?.(),
      };
    }

    return {
      text: isLastActivity ? t('rateYourself') : t('next'),
      showArrow: !isLastActivity,
      onClick: isLastActivity ? emitOnExitWithCanDo : emitOnExit,
    };
  };

  const buttonConfig = getButtonConfig();

  const nextQuizBtn = (
    <TheButton
      className={isMobile ? classNames.button : classNames.buttonTwo}
      text={buttonConfig.text}
      showArrow={buttonConfig.showArrow}
      emitOnClick={buttonConfig.onClick}
    />
  );

  let bannerTitle = t('complete');
  let descriptionText = t('unfortunately');

  const textData = isQuizPassedBefore
    ? getResultAfterFirstTry(
        correctAnswers,
        userData?.cumulativeCorrectAnswers,
        totalQuestions,
        totalScore,
        hasFailed,
        isVulgarFailure
      )
    : getResultContent(totalQuestions, correctAnswers, totalScore);

  let titleText = textData.title;
  descriptionText = textData.content;

  if (isSkipped && score === 0) {
    bannerTitle = t('skippedPopUpSummary.speak.bannerTitle');
    titleText = t('skippedPopUpSummary.speak.title');
    descriptionText = t('skippedPopUpSummary.speak.description');
  }

  const coinsText = !coins ? t('noExtraCoins') : `+${coins} ${t('extraCoins')}`;

  const setSummaryHeight = () => {
    if (selectedType !== SkillTypes.Spelling && tip) {
      return `calc(100% - ${offsetFromBottom}px)`;
    }
    return '60vh';
  };
  const language =
    interfaceLanguage?.interfaceLanguage === 'Portuguese'
      ? 'Portuguese'
      : interfaceLanguage?.interfaceLanguage === 'Spanish'
        ? 'Spanish'
        : '';
  return (
    <div
      className={classes(
        classNames.quizSummary,
        tip && classNames.hasTip,
        isTeacher && classNames.isTeacher
      )}
    >
      <div className={classNames.images}>
        <img
          className={classNames.stars}
          src={commonUtils.getStarImage(starsAmount)?.img ?? ''}
          alt='stars'
        />
        <div
          className={classNames.result}
          style={{ backgroundImage: `url(${hasFailed ? failed : complete})` }}
        >
          <div className={classNames.text}>
            <p>{selectedQuiz ? commonUtils.getName(selectedQuiz, localLang) : t('notAvailable')}</p>
            <h3>{hasFailed ? `${t('oops')}` : bannerTitle}</h3>
          </div>
        </div>
      </div>
      <div className={classNames.content}>
        <div
          ref={summaryRef}
          className={classes(classNames.summary)}
          style={{ height: setSummaryHeight() }}
        >
          {isOpenWriting && noGrading && !isVulgarFailure ? (
            <>
              <h2 className={classNames.submissionTitle}>{t('thanksForSubmission')}</h2>
              {isMobile && (
                <TheButton
                  className={classNames.button}
                  text={t('nextQuiz')}
                  showArrow={!hasFailed}
                  emitOnClick={() => emitOnClosePopUp?.(hasFailed)}
                />
              )}
            </>
          ) : (
            <>
              <h3 className={classNames.title}>{titleText}</h3>
              <div
                className={classNames.description}
                dangerouslySetInnerHTML={{ __html: descriptionText }}
              ></div>
              {isStudent && !isSkipped && (
                <div className={classNames.reward}>
                  {isDesktop && !!coins && (
                    <span className={classNames.rewardText}>{t('reward')}</span>
                  )}
                  <SVG src={coins ? coinsRow : coinsRowFailed} />
                  <span className={classNames.extraCoins}>{coinsText}</span>
                </div>
              )}
              {tip && (
                <div className={classNames.quizTip}>
                  <QuizTip
                    tip={tip ?? ''}
                    className={classNames.tip}
                    lampClassName={classes(
                      classNames.lamp,
                      classNames[`lamp-${interfaceLanguage?.direction}`]
                    )}
                    shouldExpand={true}
                  />
                </div>
              )}
              {isMobile && nextQuizBtn}
            </>
          )}
        </div>
        {isDesktop && (
          <div className={classNames.buttonsWrapper}>
            <div className={classes(classNames.buttons, classNames[language])}>
              {!(!hasNextQuiz && !hasFailed && (isLastActivity || isTeacher)) && (
                <TheButton
                  className='flex-1'
                  plain
                  text={t('backHome')}
                  emitOnClick={hasFailed || !isLastActivity ? emitOnExit : emitOnExitWithCanDo}
                />
              )}
              {nextQuizBtn}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizSummary;
